<template>
    <!-- Filters Bar -->
    <div class="mb-4 lg:mb-6 w-full">
        <div class="filters-bar flex flex-wrap items-center gap-2">
            <!-- Search Input -->
            <div class="flex-1 min-w-[200px]">
                <IconField :class="['search-field', { 'filter-active': modelValue.url && modelValue.url.length > 0 }]">
                    <InputIcon class="pi pi-search" />
                    <InputText
                        :modelValue="modelValue.url"
                        @update:modelValue="updateFilter('url', $event)"
                        :placeholder="$t('Search by domain...')"
                        class="w-full"
                        size="small"
                    />
                </IconField>
            </div>

            <!-- Add Filters Button -->
            <Button
                :label="`${$t('Add filters')} (${activeFiltersCount})`"
                icon="pi pi-filter"
                severity="secondary"
                outlined
                size="small"
                @click="$emit('update:filtersModalVisible', true)"
                :class="['filter-button', { 'filter-active-button': activeFiltersCount > 0 }]"
            />

            <!-- Project Select (Client only) -->
            <MultiSelect
                v-if="showProjectFilter"
                :modelValue="modelValue.project_id"
                @update:modelValue="updateFilter('project_id', $event)"
                :options="projects"
                optionLabel="name"
                optionValue="id"
                :placeholder="$t('Projects')"
                :class="['min-w-[150px]', { 'filter-active': modelValue.project_id && modelValue.project_id.length > 0 }]"
                :pt="{
                    root: {
                        style: modelValue.project_id && modelValue.project_id.length > 0
                            ? 'background-color: rgba(59, 130, 246, 0.1) !important; border: 1px solid rgb(59, 130, 246, 0.5) !important;'
                            : ''
                    }
                }"
                size="small"
                :showToggleAll="false"
            >
                <template #value="slotProps">
                    <span v-if="slotProps.value && slotProps.value.length === 1">
                        {{ projects.find(p => p.id === slotProps.value[0])?.name }}
                    </span>
                    <span v-else-if="slotProps.value && slotProps.value.length > 1">
                        {{ slotProps.value.length }} {{ $t('projects selected') }}
                    </span>
                    <span v-else>{{ $t('Projects') }}</span>
                </template>
                <template #option="slotProps">
                    <div class="flex items-center gap-2">
                        <div
                            class="w-5 h-5 rounded flex items-center justify-center text-white font-semibold text-[10px]"
                            :style="{ backgroundColor: slotProps.option.color }"
                        >
                            {{ slotProps.option.name.charAt(0).toUpperCase() }}
                        </div>
                        <span class="text-sm">{{ slotProps.option.name }}</span>
                    </div>
                </template>
            </MultiSelect>

            <!-- Country Select with Map Button -->
            <div class="flex gap-1 items-center">
                <MultiSelect
                    :modelValue="modelValue.country_id"
                    @update:modelValue="updateFilter('country_id', $event)"
                    :options="countries"
                    optionLabel="name"
                    optionValue="id"
                    :placeholder="$t('Country')"
                    :class="['min-w-[150px] max-w-[275px]', { 'filter-active': modelValue.country_id && modelValue.country_id.length > 0 }]"
                    :pt="{
                        root: {
                            style: modelValue.country_id && modelValue.country_id.length > 0
                                ? 'background-color: rgba(59, 130, 246, 0.1) !important; border: 1px solid rgb(59, 130, 246, 0.5) !important;'
                                : ''
                        }
                    }"
                    size="small"
                />
                <Button
                    icon="pi pi-globe"
                    severity="secondary"
                    outlined
                    size="small"
                    @click="showMapModal = true"
                    v-tooltip.top="$t('Select country from map')"
                    :class="['filter-button', { 'filter-active-button': modelValue.country_id && modelValue.country_id.length > 0 }]"
                />
            </div>

            <!-- Category Select -->
            <MultiSelect
                :modelValue="modelValue.category_id"
                @update:modelValue="updateFilter('category_id', $event)"
                :options="categories"
                optionLabel="title"
                optionValue="id"
                :placeholder="$t('Category')"
                :class="['min-w-[150px]', { 'filter-active': modelValue.category_id && modelValue.category_id.length > 0 }]"
                :pt="{
                    root: {
                        style: modelValue.category_id && modelValue.category_id.length > 0
                            ? 'background-color: rgba(59, 130, 246, 0.1) !important; border: 1px solid rgb(59, 130, 246, 0.5) !important;'
                            : ''
                    }
                }"
                size="small"
            />

            <Button
                type="button"
                icon="pi pi-angle-down"
                iconPos="right"
                :label="$t('Traffic Range')"
                severity="secondary"
                outlined
                size="small"
                @click="toggleTrafficRange"
                :class="['filter-button', { 'filter-active-button': modelValue.traffic.from || modelValue.traffic.to }]"
            />
            <Popover ref="trafficRangevisibility">
                <div class="flex gap-1 items-center min-w-[200px]">
                    <InputNumber
                        :modelValue="modelValue.traffic.from"
                        @update:modelValue="updateNestedFilter('traffic', 'from', $event)"
                        :placeholder="$t('Min Traffic')"
                        class="flex-1"
                        size="small"
                        :inputStyle="{width: '100%'}"
                        :min="0"
                    />
                    <span class="text-surface-400">-</span>
                    <InputNumber
                        :modelValue="modelValue.traffic.to"
                        @update:modelValue="updateNestedFilter('traffic', 'to', $event)"
                        :placeholder="$t('Max Traffic')"
                        class="flex-1"
                        size="small"
                        :inputStyle="{width: '100%'}"
                        :min="0"
                    />
                </div>
            </Popover>
        </div>
    </div>

    <!-- Filters Drawer -->
    <Drawer
        :visible="filtersModalVisible"
        @update:visible="$emit('update:filtersModalVisible', $event)"
        modal
        :header="$t('Add Filters')"
        :style="{ width: '90vw', maxWidth: '35rem' }"
        :breakpoints="{ '960px': '75vw', '641px': '90vw' }"
    >
        <template #header>
            <div class="flex items-center">
                <p class="text-3xl mr-4">{{ $t('Add Filters') }}</p>
                <div class="flex gap-2">
                    <Button
                        :label="$t('Reset Filters')"
                        icon="pi pi-filter-slash"
                        severity="secondary"
                        outlined
                        size="small"
                        @click="resetFilters"
                    />
                    <Button
                        :label="expandePanels.length ? $t('Collapse all') : $t('Expand all')"
                        :icon="expandePanels.length ? 'pi pi-angle-double-up' : 'pi pi-angle-double-down'"
                        severity="secondary"
                        outlined
                        size="small"
                        @click="expandePanels.length ? closeAllAccordions() : openAllAccordions()"
                    />
                </div>

            </div>
        </template>
        <Accordion multiple v-model:value="expandePanels">
        <!-- Price Range -->
        <p class="text-xl">{{ $t('Most popular filters') }}</p>
        <AccordionPanel value="0">
            <AccordionHeader>
                <div class="flex flex-col gap-1 w-full">
                    <span class="font-medium text-surface-900">{{ $t('Price') }}</span>
                    <span
                        v-if="modelValue.sale.from || modelValue.sale.to"
                        class="text-xs text-primary-600"
                    >
                        {{ modelValue.sale.from ? formatCurrency(modelValue.sale.from) : $t('$0') }}
                        -
                        {{ modelValue.sale.to ? formatCurrency(modelValue.sale.to) : $t('Max') }}
                    </span>
                    <span v-else class="text-xs text-surface-400">{{ $t('Not selected') }}</span>
                </div>
            </AccordionHeader>
            <AccordionContent>
                <div class="flex gap-2 px-4 pb-4">
                    <div class="flex flex-col">
                        <label class="text-sm text-gray-700 mb-1">From</label>
                        <InputNumber
                            :modelValue="modelValue.sale.from"
                            @update:modelValue="updateNestedFilter('sale', 'from', $event)"
                            :placeholder="$t('Min')"
                            class="flex-1"
                            size="small"
                            :inputStyle="{width: '100%'}"
                            :min="0"
                        />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-sm text-gray-700 mb-1">To</label>
                        <InputNumber
                            :modelValue="modelValue.sale.to"
                            @update:modelValue="updateNestedFilter('sale', 'to', $event)"
                            :placeholder="$t('Max')"
                            class="flex-1"
                            size="small"
                            :inputStyle="{width: '100%'}"
                            :min="0"
                        />
                    </div>
                </div>
            </AccordionContent>
        </AccordionPanel>

        <!-- DA Range -->
        <AccordionPanel value="1">
            <AccordionHeader>
                <div class="flex flex-col gap-1 w-full">
                    <span class="font-medium text-surface-900">{{ $t('DA') }}</span>
                    <span
                        v-if="modelValue.da.from || modelValue.da.to"
                        class="text-xs text-primary-600"
                    >
                        {{ modelValue.da.from || '0' }} - {{ modelValue.da.to || '100' }}
                    </span>
                    <span v-else class="text-xs text-surface-400">{{ $t('Not selected') }}</span>
                </div>
            </AccordionHeader>
            <AccordionContent>
                <div class="flex gap-2 px-4 pb-4">
                    <div class="flex flex-col">
                        <label class="text-sm text-gray-700 mb-1">From</label>
                        <InputNumber
                            :modelValue="modelValue.da.from"
                            @update:modelValue="updateNestedFilter('da', 'from', $event)"
                            :placeholder="$t('Min')"
                            class="flex-1"
                            size="small"
                            :inputStyle="{width: '100%'}"
                            :min="0"
                            :max="100"
                        />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-sm text-gray-700 mb-1">To</label>
                        <InputNumber
                            :modelValue="modelValue.da.to"
                            @update:modelValue="updateNestedFilter('da', 'to', $event)"
                            :placeholder="$t('Max')"
                            class="flex-1"
                            size="small"
                            :inputStyle="{width: '100%'}"
                            :min="0"
                            :max="100"
                        />
                    </div>
                </div>
            </AccordionContent>
        </AccordionPanel>

        <!-- DR Range -->
        <AccordionPanel value="2">
            <AccordionHeader>
                <div class="flex flex-col gap-1 w-full">
                    <span class="font-medium text-surface-900">{{ $t('DR') }}</span>
                    <span
                        v-if="modelValue.dr.from || modelValue.dr.to"
                        class="text-xs text-primary-600"
                    >
                        {{ modelValue.dr.from || '0' }} - {{ modelValue.dr.to || '100' }}
                    </span>
                    <span v-else class="text-xs text-surface-400">{{ $t('Not selected') }}</span>
                </div>
            </AccordionHeader>
            <AccordionContent>
                <div class="flex gap-2 px-4 pb-4">
                    <div class="flex flex-col">
                        <label class="text-sm text-gray-700 mb-1">From</label>
                        <InputNumber
                            :modelValue="modelValue.dr.from"
                            @update:modelValue="updateNestedFilter('dr', 'from', $event)"
                            :placeholder="$t('Min')"
                            class="flex-1"
                            size="small"
                            :inputStyle="{width: '100%'}"
                            :min="0"
                            :max="100"
                        />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-sm text-gray-700 mb-1">To</label>
                        <InputNumber
                            :modelValue="modelValue.dr.to"
                            @update:modelValue="updateNestedFilter('dr', 'to', $event)"
                            :placeholder="$t('Max')"
                            class="flex-1"
                            size="small"
                            :inputStyle="{width: '100%'}"
                            :min="0"
                            :max="100"
                        />
                    </div>
                </div>
            </AccordionContent>
        </AccordionPanel>

        <!-- Traffic Range -->
        <AccordionPanel value="3">
            <AccordionHeader>
                <div class="flex flex-col gap-1 w-full">
                    <span class="font-medium text-surface-900">{{ $t('Traffic') }}</span>
                    <span
                        v-if="modelValue.traffic.from || modelValue.traffic.to"
                        class="text-xs text-primary-600"
                    >
                        {{ modelValue.traffic.from?.toLocaleString() || $t('Min') }} - {{ modelValue.traffic.to?.toLocaleString() || $t('Max') }}
                    </span>
                    <span v-else class="text-xs text-surface-400">{{ $t('Not selected') }}</span>
                </div>
            </AccordionHeader>
            <AccordionContent>
                <div class="flex gap-2 px-4 pb-4">
                    <div class="flex flex-col">
                        <label class="text-sm text-gray-700 mb-1">From</label>
                        <InputNumber
                            :modelValue="modelValue.traffic.from"
                            @update:modelValue="updateNestedFilter('traffic', 'from', $event)"
                            :placeholder="$t('Min')"
                            class="flex-1"
                            size="small"
                            :inputStyle="{width: '100%'}"
                            :min="0"
                        />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-sm text-gray-700 mb-1">To</label>
                        <InputNumber
                            :modelValue="modelValue.traffic.to"
                            @update:modelValue="updateNestedFilter('traffic', 'to', $event)"
                            :placeholder="$t('Max')"
                            class="flex-1"
                            size="small"
                            :inputStyle="{width: '100%'}"
                            :min="0"
                        />
                    </div>
                </div>
            </AccordionContent>
        </AccordionPanel>
        <p class="mt-6 text-xl">{{ $t('Other filters') }}</p>
        <!-- Language -->
        <AccordionPanel value="4">
            <AccordionHeader>
                <div class="flex flex-col gap-1 w-full">
                    <span class="font-medium text-surface-900">{{ $t('Language') }}</span>
                    <span
                        v-if="modelValue.language_id && modelValue.language_id.length > 0"
                        class="text-xs text-primary-600"
                    >
                        {{ languages.filter(l => modelValue.language_id.includes(l.id)).map(l => l.name).join(', ') }}
                    </span>
                    <span v-else class="text-xs text-surface-400">{{ $t('Not selected') }}</span>
                </div>
            </AccordionHeader>
            <AccordionContent>
                <div class="px-4 pb-4">
                    <MultiSelect
                        :modelValue="modelValue.language_id"
                        @update:modelValue="updateFilter('language_id', $event)"
                        :options="languages"
                        optionLabel="name"
                        optionValue="id"
                        :placeholder="$t('Select...')"
                        class="w-full"
                        size="small"
                    />
                </div>
            </AccordionContent>
        </AccordionPanel>

        <!-- Country -->
        <AccordionPanel value="5">
            <AccordionHeader>
                <div class="flex flex-col gap-1 w-full">
                    <span class="font-medium text-surface-900">{{ $t('Country') }}</span>
                    <span
                        v-if="modelValue.country_id && modelValue.country_id.length > 0"
                        class="text-xs text-primary-600"
                    >
                        {{ countries.filter(c => modelValue.country_id.includes(c.id)).map(c => c.name).join(', ') }}
                    </span>
                    <span v-else class="text-xs text-surface-400">{{ $t('Not selected') }}</span>
                </div>
            </AccordionHeader>
            <AccordionContent>
                <div class="px-4 pb-4">
                    <MultiSelect
                        :modelValue="modelValue.country_id"
                        @update:modelValue="updateFilter('country_id', $event)"
                        :options="countries"
                        optionLabel="name"
                        optionValue="id"
                        :placeholder="$t('Select...')"
                        class="w-full"
                        size="small"
                    />
                </div>
            </AccordionContent>
        </AccordionPanel>

        <!-- Category -->
        <AccordionPanel value="6">
            <AccordionHeader>
                <div class="flex flex-col gap-1 w-full">
                    <span class="font-medium text-surface-900">{{ $t('Category') }}</span>
                    <span
                        v-if="modelValue.category_id && modelValue.category_id.length > 0"
                        class="text-xs text-primary-600"
                    >
                        {{ categories.filter(c => modelValue.category_id.includes(c.id)).map(c => c.title).join(', ') }}
                    </span>
                    <span v-else class="text-xs text-surface-400">{{ $t('Not selected') }}</span>
                </div>
            </AccordionHeader>
            <AccordionContent>
                <div class="px-4 pb-4">
                    <MultiSelect
                        :modelValue="modelValue.category_id"
                        @update:modelValue="updateFilter('category_id', $event)"
                        :options="categories"
                        optionLabel="title"
                        optionValue="id"
                        :placeholder="$t('Select...')"
                        class="w-full"
                        size="small"
                    />
                </div>
            </AccordionContent>
        </AccordionPanel>

        <!-- Seller (Admin only) -->
        <AccordionPanel v-if="showSellerFilter" value="7">
            <AccordionHeader>
                <div class="flex flex-col gap-1 w-full">
                    <span class="font-medium text-surface-900">{{ $t('Seller') }}</span>
                    <span
                        v-if="modelValue.seller_id && modelValue.seller_id.length > 0"
                        class="text-xs text-primary-600"
                    >
                        {{ sellers.filter(s => modelValue.seller_id.includes(s.id)).map(s => s.name).join(', ') }}
                    </span>
                    <span v-else class="text-xs text-surface-400">{{ $t('Not selected') }}</span>
                </div>
            </AccordionHeader>
            <AccordionContent>
                <div class="px-4 pb-4">
                    <MultiSelect
                        :modelValue="modelValue.seller_id"
                        @update:modelValue="updateFilter('seller_id', $event)"
                        :options="sellers"
                        optionLabel="name"
                        optionValue="id"
                        :placeholder="$t('Select...')"
                        class="w-full"
                        size="small"
                    />
                </div>
            </AccordionContent>
        </AccordionPanel>

        <!-- Types -->
        <AccordionPanel value="8">
            <AccordionHeader>
                <div class="flex flex-col gap-1 w-full">
                    <span class="font-medium text-surface-900">{{ $t('Niches') }}</span>
                    <span
                        v-if="modelValue.types && modelValue.types.length > 0"
                        class="text-xs text-primary-600"
                    >
                        {{ types.filter(t => modelValue.types.includes(t.id)).map(t => t.name).join(', ') }}
                    </span>
                    <span v-else class="text-xs text-surface-400">{{ $t('Not selected') }}</span>
                </div>
            </AccordionHeader>
            <AccordionContent>
                <div class="px-4 pb-4">
                    <MultiSelect
                        :modelValue="modelValue.types"
                        @update:modelValue="updateFilter('types', $event)"
                        :options="types"
                        optionLabel="name"
                        optionValue="id"
                        :placeholder="$t('Select...')"
                        class="w-full"
                        size="small"
                        display="chip"
                    />
                </div>
            </AccordionContent>
        </AccordionPanel>

        <AccordionPanel value="9">
            <AccordionHeader>
                <div class="flex flex-col gap-1 w-full">
                    <span class="font-medium text-surface-900">{{ $t('Link Type') }}</span>
                    <span
                        v-if="(Array.isArray(modelValue.link_type) && modelValue.link_type.length > 0) || (!Array.isArray(modelValue.link_type) && modelValue.link_type)"
                        class="text-xs text-primary-600"
                    >
                        {{ 
                            Array.isArray(modelValue.link_type) 
                                ? modelValue.link_type.map(lt => linkTypeOptions.find(opt => opt.value === lt)?.label).join(', ')
                                : linkTypeOptions.find(opt => opt.value === modelValue.link_type)?.label
                        }}
                    </span>
                    <span v-else class="text-xs text-surface-400">{{ $t('Not selected') }}</span>
                </div>
            </AccordionHeader>
            <AccordionContent>
                <div class="px-4 pb-4">
                    <MultiSelect
                        :modelValue="modelValue.link_type"
                        @update:modelValue="updateFilter('link_type', $event)"
                        :options="linkTypeOptions"
                        optionLabel="label"
                        optionValue="value"
                        :placeholder="$t('Select...')"
                        class="w-full"
                        size="small"
                    />
                </div>
            </AccordionContent>
        </AccordionPanel>

        <!-- Toggle Filters (Options) -->
        <AccordionPanel value="10">
            <AccordionHeader>
                <div class="flex flex-col gap-1 w-full">
                    <span class="font-medium text-surface-900">{{ $t('Options') }}</span>
                    <span
                        v-if="hasActiveOptions"
                        class="text-xs text-primary-600"
                    >
                        {{ activeOptionsText }}
                    </span>
                    <span v-else class="text-xs text-surface-400">{{ $t('Not selected') }}</span>
                </div>
            </AccordionHeader>
            <AccordionContent>
                <div class="flex flex-col gap-3 p-4">
                    <!-- Client-specific options -->
                    <div v-if="showFavoritesFilter" class="flex items-center justify-between">
                        <label class="text-sm font-medium">{{ $t('Favorites') }}</label>
                        <ToggleSwitch
                            :modelValue="modelValue.favorites"
                            @update:modelValue="updateFilter('favorites', $event)"
                        />
                    </div>
                    <div v-if="showInterestsFilter" class="flex items-center justify-between">
                        <label class="text-sm font-medium">{{ $t('Interests') }}</label>
                        <ToggleSwitch
                            :modelValue="modelValue.interests"
                            @update:modelValue="updateFilter('interests', $event)"
                        />
                    </div>

                    <!-- Common options -->
                    <div class="flex items-center justify-between">
                        <label class="text-sm font-medium">{{ $t('Recommended') }}</label>
                        <ToggleSwitch
                            :modelValue="modelValue.recommended"
                            @update:modelValue="updateFilter('recommended', $event)"
                        />
                    </div>
                    <div class="flex items-center justify-between">
                        <label class="text-sm font-medium">{{ $t('Gambling') }}</label>
                        <ToggleSwitch
                            :modelValue="modelValue.gambling"
                            @update:modelValue="updateFilter('gambling', $event)"
                        />
                    </div>
                    <!-- <div class="flex items-center justify-between">
                        <label class="text-sm font-medium">{{ $t('Not sponsored') }}</label>
                        <ToggleSwitch
                            :modelValue="modelValue.sponsor"
                            @update:modelValue="updateFilter('sponsor', $event)"
                        />
                    </div>
                    <div class="flex items-center justify-between">
                        <label class="text-sm font-medium">{{ $t('Follow') }}</label>
                        <ToggleSwitch
                            :modelValue="modelValue.follow"
                            @update:modelValue="updateFilter('follow', $event)"
                        />
                    </div> -->
                    <div class="flex items-center justify-between">
                        <label class="text-sm font-medium">{{ $t('New') }}</label>
                        <ToggleSwitch
                            :modelValue="modelValue.new"
                            @update:modelValue="updateFilter('new', $event)"
                        />
                    </div>
                </div>
            </AccordionContent>
        </AccordionPanel>
        </Accordion>
    </Drawer>

    <!-- Map Modal -->
    <Dialog
        v-model:visible="showMapModal"
        :header="$t('Select Country from Map')"
        modal
        :style="{ width: '90vw', maxWidth: '1200px' }"
        :breakpoints="{ '960px': '95vw' }"
    >
        <div class="map-modal-content">
            <p class="text-sm text-surface-600 mb-4">{{ $t('Click on a country in the map to filter sites by that country') }}</p>
            <div class="flex justify-center">
                <div id="country_filter_map" class="w-full" style="min-height: 500px;"></div>
            </div>
        </div>
    </Dialog>
</template>

<script setup>
import { computed, getCurrentInstance, ref, watch, nextTick } from 'vue';
import Button from 'primevue/button';
import MultiSelect from 'primevue/multiselect';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import Drawer from 'primevue/drawer';
import Dialog from 'primevue/dialog';
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';
import ToggleSwitch from 'primevue/toggleswitch';
import Popover from 'primevue/popover';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const props = defineProps({
    modelValue: {
        type: Object,
        required: true
    },
    filtersModalVisible: {
        type: Boolean,
        default: false
    },
    countries: {
        type: Array,
        default: () => []
    },
    languages: {
        type: Array,
        default: () => []
    },
    categories: {
        type: Array,
        default: () => []
    },
    sellers: {
        type: Array,
        default: () => []
    },
    types: {
        type: Array,
        default: () => []
    },
    projects: {
        type: Array,
        default: () => []
    },
    sitesByCountry: {
        type: Array,
        default: () => []
    },
    coinStore: {
        type: Object,
        required: true
    },
    // Context flags
    showProjectFilter: {
        type: Boolean,
        default: false
    },
    showFavoritesFilter: {
        type: Boolean,
        default: false
    },
    showInterestsFilter: {
        type: Boolean,
        default: false
    },
    showSellerFilter: {
        type: Boolean,
        default: false
    }
});

const expandePanels = ref([]);
const trafficRangevisibility = ref(false);
const toggleTrafficRange = (event) => {
    trafficRangevisibility.value.toggle(event);
}

const linkTypeOptions = computed(() => [
    { label: $t('DoFollow'), value: 'dofollow' },
    { label: $t('Sponsor'), value: 'sponsor' },
    { label: $t('NoFollow'), value: 'nofollow' }
]);

// Map modal state
const showMapModal = ref(false);
const googleChartsLoaded = ref(false);
const currentChart = ref(null);

// Prepare data for Google GeoChart
const prepareMapData = () => {
    // If sitesByCountry is provided from backend, use it directly
    if (props.sitesByCountry && props.sitesByCountry.length > 0) {
        return props.sitesByCountry;
    }

    // Fallback: prepare data from countries prop
    const countryCount = {};

    props.countries.forEach(country => {
        const code = country.code;
        if (code) {
            countryCount[code] = (countryCount[code] || 0) + 1;
        }
    });

    // Convert to array format for Google Charts
    const data = [['Country', 'Sites', 'Country ID']];
    props.countries.forEach(country => {
        if (country.code) {
            data.push([country.code, countryCount[country.code] || 1, country.id]);
        }
    });

    return data;
};

// Load Google Charts library
const loadGoogleCharts = () => {
    if (googleChartsLoaded.value || typeof google !== 'undefined' && google.charts) {
        drawMap();
        return;
    }

    const script = document.createElement('script');
    script.src = 'https://www.gstatic.com/charts/loader.js';
    script.async = true;
    script.onload = () => {
        google.charts.load('current', { 'packages': ['geochart'] });
        google.charts.setOnLoadCallback(() => {
            googleChartsLoaded.value = true;
            drawMap();
        });
    };
    script.onerror = () => {
        console.error('Failed to load Google Charts');
    };
    document.head.appendChild(script);
};

// Draw the map
const drawMap = () => {
    nextTick(() => {
        const mapElement = document.getElementById('country_filter_map');
        if (!mapElement) return;

        try {
            const mapData = prepareMapData();
            const data = google.visualization.arrayToDataTable(mapData);

            const options = {
                backgroundColor: 'transparent',
                colorAxis: { colors: ['#e8f0fe', '#1976d2'] },
                datalessRegionColor: '#f5f5f5',
                defaultColor: '#f5f5f5',
                width: '100%',
                height: 500,
                keepAspectRatio: true,
                legend: {
                    textStyle: {
                        color: '#495057',
                        fontSize: 12
                    }
                },
                tooltip: {
                    textStyle: {
                        color: '#495057',
                        fontSize: 12
                    },
                    trigger: 'focus'
                }
            };

            const chart = new google.visualization.GeoChart(mapElement);
            currentChart.value = chart;

            // Add click event listener
            google.visualization.events.addListener(chart, 'select', () => {
                const selection = chart.getSelection();
                if (selection.length > 0) {
                    const selectedRow = selection[0].row + 1; // Skip header row
                    const countryId = mapData[selectedRow][2]; // ID is at index 2

                    // Update filter with selected country
                    const currentCountries = Array.isArray(props.modelValue.country_id)
                        ? [...props.modelValue.country_id]
                        : [];

                    // Toggle country selection
                    if (currentCountries.includes(countryId)) {
                        // Remove if already selected
                        const index = currentCountries.indexOf(countryId);
                        currentCountries.splice(index, 1);
                    } else {
                        // Add to selection
                        currentCountries.push(countryId);
                    }

                    updateFilter('country_id', currentCountries);

                    // Close modal after selection
                    showMapModal.value = false;
                }
            });

            chart.draw(data, options);

            // Redraw on window resize
            const resizeHandler = () => {
                if (showMapModal.value && chart) {
                    chart.draw(data, options);
                }
            };
            window.addEventListener('resize', resizeHandler);
        } catch (error) {
            console.error('Error drawing map:', error);
        }
    });
};

// Watch for modal visibility changes
watch(showMapModal, (newValue) => {
    if (newValue) {
        // Load and draw map when modal opens
        if (typeof google !== 'undefined' && google.charts && googleChartsLoaded.value) {
            drawMap();
        } else {
            loadGoogleCharts();
        }
    }
});

const openAllAccordions = () => {
    expandePanels.value = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
}

const closeAllAccordions = () => {
    expandePanels.value = [];
}

const formatNumber = (number) => {
    if(number === null || number === '' || number === undefined) return '';
    const toNumber = Number(number);
    return toNumber.toLocaleString('pt-BR');
}

const emit = defineEmits(['update:modelValue', 'update:filtersModalVisible', 'reset-filters']);

// Active filters count
const activeFiltersCount = computed(() => {
    let count = 0;

    // Check range filters
    if (props.modelValue.sale.from || props.modelValue.sale.to) count++;
    if (props.modelValue.da.from || props.modelValue.da.to) count++;
    if (props.modelValue.dr.from || props.modelValue.dr.to) count++;
    if (props.modelValue.traffic.from || props.modelValue.traffic.to) count++;

    // Check boolean filters
    if (props.modelValue.sponsor) count++;
    if (props.modelValue.follow) count++;
    if (props.modelValue.recommended) count++;
    if (props.modelValue.new) count++;
    if (props.modelValue.gambling) count++;

    // Client-specific filters
    if (props.showFavoritesFilter && props.modelValue.favorites) count++;
    if (props.showInterestsFilter && props.modelValue.interests) count++;

    // Check array filters
    if (props.modelValue.language_id && props.modelValue.language_id.length > 0) count++;
    if (props.modelValue.country_id && props.modelValue.country_id.length > 0) count++;
    if (props.modelValue.category_id && props.modelValue.category_id.length > 0) count++;
    if (props.showSellerFilter && props.modelValue.seller_id && props.modelValue.seller_id.length > 0) count++;
    if (props.modelValue.types && props.modelValue.types.length > 0) count++;
    if (Array.isArray(props.modelValue.link_type) ? props.modelValue.link_type.length > 0 : props.modelValue.link_type) count++;

    // Client-specific project filter
    if (props.showProjectFilter && props.modelValue.project_id && props.modelValue.project_id.length > 0) count++;

    return count;
});

// Check if any options are active
const hasActiveOptions = computed(() => {
    return props.modelValue.recommended ||
           props.modelValue.gambling ||
           props.modelValue.sponsor ||
           props.modelValue.follow ||
           props.modelValue.new ||
           (props.showFavoritesFilter && props.modelValue.favorites) ||
           (props.showInterestsFilter && props.modelValue.interests);
});

// Build active options text
const activeOptionsText = computed(() => {
    const options = [];

    if (props.showFavoritesFilter && props.modelValue.favorites) options.push($t('Favorites'));
    if (props.showInterestsFilter && props.modelValue.interests) options.push($t('Interests'));
    if (props.modelValue.recommended) options.push($t('Recommended'));
    if (props.modelValue.gambling) options.push($t('Gambling'));
    if (props.modelValue.sponsor) options.push($t('Not sponsored'));
    if (props.modelValue.follow) options.push($t('Follow'));
    if (props.modelValue.new) options.push($t('New'));

    return options.join(', ');
});

// Format currency
const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: props.coinStore.coin,
        minimumFractionDigits: 0
    }).format(value);
};

// Update filter
const updateFilter = (key, value) => {
    const updated = { ...props.modelValue, [key]: value };
    emit('update:modelValue', updated);
};

// Update nested filter (for range filters like sale, da, dr, traffic)
const updateNestedFilter = (parent, key, value) => {
    const updated = {
        ...props.modelValue,
        [parent]: {
            ...props.modelValue[parent],
            [key]: value
        }
    };
    emit('update:modelValue', updated);
};

const resetFilters = () => {
    emit('reset-filters');
};
</script>

<style scoped>
/* Match search input height to MultiSelect dropdowns */
.search-field :deep(.p-inputtext) {
    height: 31px !important;
}

/* Active search input highlighting */
.search-field.filter-active :deep(.p-inputtext) {
    background: rgba(59, 130, 246, 0.1) !important;
    border: 1px solid rgb(59, 130, 246, 0.5) !important;
    color: rgb(59, 130, 246) !important;
}

.search-field.filter-active :deep(.p-inputtext:hover) {
    background: rgba(59, 130, 246, 0.15) !important;
}

.search-field.filter-active :deep(.p-inputtext:focus) {
    border: 1px solid rgb(59, 130, 246, 0.5) !important;
    box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25) !important;
}

.search-field.filter-active :deep(.p-icon) {
    color: rgb(59, 130, 246) !important;
}

/* Match filter buttons to PrimeVue MultiSelect styling */
.filter-button {
    height: 31px !important;
    margin-top: 1px;
}

.filter-button:deep(.p-button) {
    height: 100%;
    /* Match the exact shadow of MultiSelect/outlined components */
    box-shadow: inset 0 0 0 5px rgba(0, 0, 0, 0.12) !important;
}

.filter-button:hover:deep(.p-button) {
    box-shadow: inset 0 0 0 5px rgba(0, 0, 0, 0.12) !important;
}

.filter-button.p-button-outlined.p-button-secondary {
  border: 1px solid #ced4da !important;
}

/* Prevent MultiSelect from expanding - keep fixed width and limit text */
:deep(.p-multiselect) {
    max-width: 100%;
    height: 31px !important;
    min-height: 31px !important;
}

:deep(.p-multiselect-label) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 200px;
    display: inline-block;
    padding-left: 0.5rem !important;
}

:deep(.p-multiselect-label-container) {
    overflow: hidden;
    flex: 1;
    max-width: calc(100% - 40px);
}

:deep(.p-multiselect-trigger) {
    width: 31px !important;
}

/* Active filter highlighting for MultiSelect - MAXIMUM specificity to override PrimeVue */
.filter-active :deep(.p-multiselect),
.filter-active :deep(.p-multiselect.p-component),
.filter-active :deep(.p-multiselect.p-inputwrapper),
.filter-active :deep(div.p-multiselect),
.filter-active :deep(.p-multiselect.p-variant-outlined) {
    background-color: rgba(59, 130, 246, 0.1) !important;
    background-image: none !important;
    border-color: rgb(59, 130, 246, 0.5) !important;
    border-style: solid !important;
    border-width: 1px !important;
    box-shadow: inset 0 0 0 1px rgb(59, 130, 246, 0.5) !important;
}


.filter-active :deep(.p-multiselect:focus),
.filter-active :deep(.p-multiselect.p-component:focus),
.filter-active :deep(.p-multiselect.p-inputwrapper:focus),
.filter-active :deep(div.p-multiselect:focus) {
    border-color: rgb(59, 130, 246, 0.5) !important;
    box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25) !important;
    outline: 0 none !important;
}

.filter-active :deep(.p-multiselect-label),
.filter-active :deep(.p-multiselect-trigger),
.filter-active :deep(.p-multiselect-trigger-icon),
.filter-active :deep(.p-icon) {
    color: rgb(59, 130, 246) !important;
}

/* Active filter highlighting for Buttons - need high specificity to override PrimeVue */
.filter-active-button.p-button-secondary.p-button-outlined,
.filter-active-button:deep(.p-button.p-button-secondary.p-button-outlined),
.filter-active-button :deep(.p-button.p-button-secondary.p-button-outlined) {
    background: rgba(59, 130, 246, 0.1) !important;
    border: 0px solid rgb(59, 130, 246, 0.5) !important;
    color: rgb(59, 130, 246) !important;
    box-shadow: inset 0 0 0 1px rgb(59, 130, 246, 0.5) !important;
}

.filter-active-button.p-button-secondary.p-button-outlined .p-button-label,
.filter-active-button.p-button-secondary.p-button-outlined .p-button-icon,
.filter-active-button:deep(.p-button.p-button-secondary.p-button-outlined .p-button-label),
.filter-active-button:deep(.p-button.p-button-secondary.p-button-outlined .p-button-icon) {
    color: rgb(59, 130, 246, 0.5) !important;
}

.map-modal-content :deep(#country_filter_map) svg path {
    cursor: pointer;
    transition: opacity 0.2s ease;
}

.map-modal-content :deep(#country_filter_map) svg path:hover {
    opacity: 0.8;
}

@media (max-width: 768px) {
    .map-modal-content :deep(#country_filter_map) {
        width: 100%;
        min-height: 400px;
    }
}
</style>
